import { useEffect, useMemo, useState } from 'react'
import { CalendarClock, CalendarPlus, ChevronDown, ChevronUp, Plus, Repeat, RotateCcw, Trash2, X } from 'lucide-react'
import { newOccurrence, sortOccurrences } from '../lib/eventSnapshot'
import {
  WEEKDAY_LABELS,
  WEEK_FROM_MONDAY,
  addDays,
  describePattern,
  extendSessions,
  planSessions,
  plannedAt,
  saoPauloParts,
  todayInSaoPaulo,
  weekdayOf,
  weeklyPattern,
  withoutExisting,
  type DayTimes,
  type SessionTime,
} from '../lib/schedule'
import { holidayOn } from '../lib/holidays'
import { EditedTag, isHandEdited } from './EditedTag'
import { RemoveSessionsDialog } from './RemoveSessionsDialog'
import type { EventOccurrence, EventRun, EventStatus } from '../types'

type Props = {
  run: EventRun
  onOccurrences: (occurrences: EventOccurrence[]) => void
  onUpdateOccurrence: (occurrenceId: string, patch: Partial<EventOccurrence>) => void
  // A session fixed by hand returns to the automatic collection.
  onFollowSource?: (occurrenceId: string) => void
  // Where the place is, for its holidays.
  city?: string | null
  // The sessions already published: only they have a status to change, and
  // only a published season can be extended (PRD.md, items 16 and 17).
  savedSessions?: Set<string>
  // How many times each session went to a phone's calendar (PRD.md, item 107).
  calendarAdds?: Record<string, number>
}

const formatShortDate = (date: string) => {
  const [year, month, day] = date.split('-').map(Number)
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, day)))
}

// A session as Google Calendar writes it (PRD.md, item 18): a day, a start and
// an optional end, in São Paulo time; an end at or before the start is the
// next day. A day or time still missing leaves the session without a start,
// which publishing refuses, while the row keeps what was typed.
const fieldsOf = (occurrence: EventOccurrence) => ({
  date: occurrence.starts_at ? saoPauloParts(occurrence.starts_at).date : '',
  start: occurrence.starts_at ? saoPauloParts(occurrence.starts_at).time : '',
  end: occurrence.ends_at ? saoPauloParts(occurrence.ends_at).time : '',
})

function SessionFields({ occurrence, initialDate, focus, onChange }: {
  occurrence: EventOccurrence
  // A new session's day, when it joins another on the same day (PRD.md, item 71).
  initialDate?: string
  // The field that takes the cursor when the row appears.
  focus?: 'date' | 'start'
  onChange: (patch: Pick<EventOccurrence, 'starts_at' | 'ends_at'>) => void
}) {
  const [fields, setFields] = useState(() => {
    const value = fieldsOf(occurrence)
    return initialDate && !value.date ? { ...value, date: initialDate } : value
  })
  useEffect(() => {
    if (!occurrence.starts_at) return
    const next = fieldsOf(occurrence)
    setFields((current) => (current.date === next.date && current.start === next.start && current.end === next.end ? current : next))
  }, [occurrence])
  const set = (patch: Partial<typeof fields>) => {
    const next = { ...fields, ...patch }
    setFields(next)
    onChange(next.date && next.start ? plannedAt(next.date, { start: next.start, end: next.end || null }) : { starts_at: '', ends_at: null })
  }
  return (
    <>
      <label className="field"><span>Data *</span><input type="date" autoFocus={focus === 'date'} value={fields.date} onChange={(event) => set({ date: event.target.value })} /></label>
      <label className="field"><span>Início *</span><input type="time" autoFocus={focus === 'start'} value={fields.start} onChange={(event) => set({ start: event.target.value })} /></label>
      <label className="field"><span>Término</span><input type="time" value={fields.end} onChange={(event) => set({ end: event.target.value })} /></label>
    </>
  )
}

const lastLiveDate = (occurrences: EventOccurrence[]) => occurrences
  .filter((item) => item.status === 'published' && item.starts_at)
  .map((item) => saoPauloParts(item.starts_at).date)
  .sort()
  .at(-1)

// A season's sessions (docs/cms-hub.md): the ones to come in time order, the
// past ones folded away, and two ways to add many at once: a plan of
// weekdays and times over a period, as Eventbrite's schedule for events with
// several dates, or the season's own weekly pattern carried to a new last day.
export function RunSchedule({ run, onOccurrences, onUpdateOccurrence, onFollowSource, city, savedSessions, calendarAdds }: Props) {
  const today = todayInSaoPaulo()
  const last = lastLiveDate(run.occurrences)
  const [panel, setPanel] = useState<'plan' | 'extend' | null>(null)
  const [showPast, setShowPast] = useState(false)
  const [from, setFrom] = useState(() => (last && last >= today ? addDays(last, 1) : today))
  const [to, setTo] = useState(() => addDays(last && last >= today ? addDays(last, 1) : today, 27))
  const [days, setDays] = useState<DayTimes>({})
  // A season that ended resumes from today, never in the weeks that passed
  // (PRD.md, item 117).
  const resumesFrom = last && last > today ? last : today
  const [until, setUntil] = useState(() => addDays(resumesFrom, 28))
  // Days taken out of the plan before adding it, and sessions picked to
  // remove together (PRD.md, item 3).
  const [skipped, setSkipped] = useState<string[]>([])
  const [selected, setSelected] = useState<string[]>([])
  // Sessions on their way out, asked first when a published one is still to
  // come (PRD.md, item 107).
  const [removing, setRemoving] = useState<string[] | null>(null)
  // The order the list had when the cursor entered it (PRD.md, item 72): rows
  // keep their place while typed in, a year or minutes half typed included,
  // and the list sorts once the cursor leaves it, as a sorted spreadsheet does.
  const [held, setHeld] = useState<{ past: string[]; coming: string[] } | null>(null)
  // A row just added, with its day when it repeats another's (PRD.md, item 71).
  const [opened, setOpened] = useState<{ id: string; date?: string } | null>(null)

  const pattern = useMemo(() => weeklyPattern(run.occurrences), [run.occurrences])
  const isSaved = (item: EventOccurrence) => Boolean(savedSessions?.has(item.id))
  const published = run.occurrences.some(isSaved)
  const chosenDays = WEEK_FROM_MONDAY.filter((weekday) => days[weekday])
  const planned = useMemo(() => withoutExisting(planSessions({ from, to, days }), run.occurrences),
    [from, to, days, run.occurrences])
  const plannedDays = useMemo(() => [...new Set(planned.map((session) => saoPauloParts(session.starts_at).date))], [planned])
  const chosen = planned.filter((session) => !skipped.includes(saoPauloParts(session.starts_at).date))
  const extension = useMemo(() => withoutExisting(extendSessions(run.occurrences, until), run.occurrences),
    [run.occurrences, until])

  // A removed session leaves the screen (PRD.md, item 28); Descartar alterações brings it back.
  const sorted = sortOccurrences(run.occurrences.filter((item) => item.status !== 'deleted'))
  const isPast = (item: EventOccurrence) => Boolean(item.starts_at) && saoPauloParts(item.starts_at).date < today
  const byId = new Map(sorted.map((item) => [item.id, item]))
  const inOrder = (ids: string[]) => ids.flatMap((id) => byId.get(id) ?? [])
  const holding = held && new Set([...held.past, ...held.coming])
  const past = held ? inOrder(held.past) : sorted.filter(isPast)
  const coming = held && holding
    ? [...sorted.filter((item) => !holding.has(item.id)), ...inOrder(held.coming)]
    : sorted.filter((item) => !isPast(item))
  const ordered = [...past, ...coming]
  const idsOf = (items: EventOccurrence[]) => items.map((item) => item.id)
  const dayOf = (item: EventOccurrence | undefined) => (item?.starts_at ? saoPauloParts(item.starts_at).date : null)
  const live = coming.filter((item) => item.status === 'published').length
  // Every session in view, to pick them all at once, as Gmail's list (PRD.md,
  // item 20); the past ones only when shown.
  const selectable = [...(showPast ? past : []), ...coming]
  const allSelected = selectable.length > 0 && selectable.every((item) => selected.includes(item.id))
  const someSelected = !allSelected && selectable.some((item) => selected.includes(item.id))

  const add = (sessions: { starts_at: string; ends_at: string | null }[]) => {
    const created = sessions.map((session) => newOccurrence(run.id, session))
    onOccurrences(sortOccurrences([...run.occurrences, ...created]))
    setPanel(null)
    setSkipped([])
    setHeld(null)
    return created
  }
  // One date, the cursor on its day.
  const addDate = () => setOpened({ id: add([{ starts_at: '', ends_at: null }])[0].id })
  // Another session on a day, right below its last one, the cursor on its
  // start (PRD.md, item 71).
  const addSameDay = (after: EventOccurrence) => {
    const session = newOccurrence(run.id, { starts_at: '', ends_at: null })
    onOccurrences([...run.occurrences, session])
    setOpened({ id: session.id, date: dayOf(after) ?? undefined })
    setHeld((current) => {
      const order = current ?? { past: idsOf(past), coming: idsOf(coming) }
      const below = (ids: string[]) => ids.flatMap((id) => (id === after.id ? [id, session.id] : [id]))
      return { past: below(order.past), coming: below(order.coming) }
    })
  }
  const toggleSkipped = (date: string) => setSkipped((current) => current.includes(date)
    ? current.filter((item) => item !== date) : [...current, date])
  const toggleSelected = (id: string) => setSelected((current) => current.includes(id)
    ? current.filter((item) => item !== id) : [...current, id])
  // A session not yet published leaves the list; a published one leaves the
  // screen and is deleted on saving, or stays, canceled.
  const remove = (ids: string[], status: 'deleted' | 'canceled' = 'deleted') => {
    onOccurrences(run.occurrences
      .filter((item) => isSaved(item) || !ids.includes(item.id))
      .map((item) => ids.includes(item.id) ? { ...item, status } : item))
    setSelected((current) => current.filter((id) => !ids.includes(id)))
    setRemoving(null)
  }
  // A published session still to come: canceling it tells who put it in
  // their calendar; deleting it doesn't.
  const upcoming = (item: EventOccurrence) => isSaved(item) && item.status === 'published'
    && Boolean(item.starts_at) && new Date(item.ends_at || item.starts_at).getTime() > Date.now()
  const askRemove = (ids: string[]) => {
    if (run.occurrences.some((item) => ids.includes(item.id) && upcoming(item))) setRemoving(ids)
    else remove(ids)
  }
  // A day chosen starts with the times of the day before it (or after, or
  // 21:00), so only what differs is changed: Sunday at 18:00.
  const toggleWeekday = (weekday: number) => setDays((current) => {
    if (current[weekday]) return Object.fromEntries(Object.entries(current).filter(([day]) => Number(day) !== weekday))
    const position = WEEK_FROM_MONDAY.indexOf(weekday)
    const near = [...WEEK_FROM_MONDAY.slice(0, position)].reverse().find((day) => current[day])
      ?? WEEK_FROM_MONDAY.slice(position + 1).find((day) => current[day])
    return { ...current, [weekday]: near === undefined ? [{ start: '21:00', end: null }] : current[near]!.map((time) => ({ ...time })) }
  })
  const setTimes = (weekday: number, change: (times: SessionTime[]) => SessionTime[]) =>
    setDays((current) => ({ ...current, [weekday]: change(current[weekday] ?? []) }))
  const updateTime = (weekday: number, index: number, patch: Partial<SessionTime>) =>
    setTimes(weekday, (times) => times.map((time, position) => (position === index ? { ...time, ...patch } : time)))

  const row = (occurrence: EventOccurrence) => {
    const position = ordered.indexOf(occurrence) + 1
    const day = dayOf(occurrence)
    const next = selectable[selectable.indexOf(occurrence) + 1]
    const lastOfDay = Boolean(day) && dayOf(next) !== day
    const opening = opened?.id === occurrence.id ? opened : null
    const holiday = occurrence.starts_at ? holidayOn(saoPauloParts(occurrence.starts_at).date, city) : null
    const edited = isHandEdited(occurrence)
    // An end on another day, written out: the next day, or a longer session.
    const endDay = occurrence.starts_at && occurrence.ends_at ? saoPauloParts(occurrence.ends_at).date : null
    const endsLater = endDay && endDay !== saoPauloParts(occurrence.starts_at).date ? endDay : null
    return (
      <div className="occurrence-row" key={occurrence.id}>
        <label className="occurrence-index" title={`Horário ${position}`}>
          <input type="checkbox" aria-label={`Selecionar horário ${position}`} checked={selected.includes(occurrence.id)} onChange={() => toggleSelected(occurrence.id)} />
        </label>
        <SessionFields occurrence={occurrence} initialDate={opening?.date} focus={opening ? (opening.date ? 'start' : 'date') : undefined}
          onChange={(patch) => onUpdateOccurrence(occurrence.id, patch)} />
        {published && (isSaved(occurrence)
          ? <label className="field occurrence-status"><span>Status</span><select value={occurrence.status} onChange={(event) => onUpdateOccurrence(occurrence.id, { status: event.target.value as EventStatus })}><option value="published">Ativo</option><option value="canceled">Cancelado</option></select></label>
          : <span className="occurrence-row__new">Novo</span>)}
        <button className="icon-button icon-button--danger occurrence-delete" onClick={() => askRemove([occurrence.id])} aria-label="Excluir horário"><Trash2 size={17} /></button>
        {(holiday || edited || endsLater) && (
          <div className="occurrence-row__note">
            {endsLater && <span>Termina {endsLater === addDays(saoPauloParts(occurrence.starts_at).date, 1) ? 'no dia seguinte' : formatShortDate(endsLater)}</span>}
            {holiday && <span className="holiday-tag">Feriado · {holiday}</span>}
            {edited && <EditedTag onFollow={onFollowSource && (() => onFollowSource(occurrence.id))} />}
          </div>
        )}
        {/* The cursor stays where it is, so the list keeps its order. */}
        {lastOfDay && <button type="button" className="text-button occurrence-row__more" onMouseDown={(event) => event.preventDefault()} onClick={() => addSameDay(occurrence)}><Plus size={15} /> Outro horário neste dia</button>}
      </div>
    )
  }

  return (
    <>
      <div className="occurrence-heading">
        <div>
          <h4>Datas e horários</h4>
          <p>{live} {live === 1 ? 'horário por vir' : 'horários por vir'}{past.length ? ` · ${past.length} ${past.length === 1 ? 'passado' : 'passados'}` : ''}{pattern.length ? ` · ${describePattern(pattern)}` : ''}</p>
        </div>
        <div className="occurrence-heading__actions">
          <button className="button button--quiet" onClick={addDate}><CalendarPlus size={17} /> Adicionar data</button>
          <button className={`button button--secondary ${panel === 'plan' ? 'is-active' : ''}`} onClick={() => setPanel(panel === 'plan' ? null : 'plan')}><Repeat size={16} /> Adicionar várias datas</button>
          {published && pattern.length > 0 && <button className={`button button--secondary ${panel === 'extend' ? 'is-active' : ''}`} onClick={() => setPanel(panel === 'extend' ? null : 'extend')}><CalendarClock size={16} /> Estender temporada</button>}
        </div>
      </div>

      {panel === 'plan' && (
        <div className="schedule-panel" aria-label="Adicionar várias datas">
          <div className="schedule-panel__grid">
            <label className="field"><span>De</span><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
            <label className="field"><span>Até</span><input type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} /></label>
          </div>
          <div className="field"><span>Dias e horários</span>
            <div className="weekday-picker" role="group" aria-label="Dias da semana">
              {WEEK_FROM_MONDAY.map((weekday) => (
                <button type="button" key={weekday} aria-pressed={Boolean(days[weekday])} className={days[weekday] ? 'is-active' : ''} onClick={() => toggleWeekday(weekday)}>{WEEKDAY_LABELS[weekday]}</button>
              ))}
            </div>
            {chosenDays.length > 0 && (
              <div className="day-times">
                {chosenDays.map((weekday) => {
                  const label = WEEKDAY_LABELS[weekday]
                  const times = days[weekday] ?? []
                  return (
                    <div className="day-times__row" key={weekday} role="group" aria-label={`Horários de ${label}`}>
                      <strong>{label}</strong>
                      <div className="time-list">
                        {times.map((time, index) => (
                          <div className="time-row" key={index}>
                            <input type="time" aria-label={`Início ${label} ${index + 1}`} value={time.start} onChange={(event) => updateTime(weekday, index, { start: event.target.value })} />
                            <span>até</span>
                            <input type="time" aria-label={`Término ${label} ${index + 1}`} value={time.end ?? ''} onChange={(event) => updateTime(weekday, index, { end: event.target.value || null })} />
                            {times.length > 1 && <button type="button" className="icon-button" aria-label={`Tirar horário ${index + 1} de ${label}`} onClick={() => setTimes(weekday, (current) => current.filter((_, position) => position !== index))}><X size={15} /></button>}
                          </div>
                        ))}
                        <button type="button" className="text-button" onClick={() => setTimes(weekday, (current) => [...current, { start: '18:00', end: null }])}><Plus size={15} /> Outro horário</button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            <small>Cada dia começa com os horários do anterior: mude só o que for diferente. O término é opcional; um término antes do início vira o dia seguinte.</small>
          </div>
          {plannedDays.length > 0 && (
            <div className="field"><span>Dias</span>
              <div className="day-picker" role="group" aria-label="Dias que serão criados">
                {plannedDays.map((date) => {
                  const holiday = holidayOn(date, city)
                  const off = skipped.includes(date)
                  return (
                    <button type="button" key={date} aria-pressed={!off} className={['day-chip', off && 'is-off', holiday && 'is-holiday'].filter(Boolean).join(' ')}
                      title={holiday ? `Feriado: ${holiday}` : undefined} onClick={() => toggleSkipped(date)}>
                      <span>{WEEKDAY_LABELS[weekdayOf(date)]} {formatShortDate(date)}{off ? <RotateCcw size={12} aria-hidden /> : <X size={12} aria-hidden />}</span>
                      {holiday && <small>{holiday}</small>}
                    </button>
                  )
                })}
              </div>
              <small>Toque num dia para tirar (✕) ou devolver (↺). Os feriados aparecem marcados, sem sair sozinhos.</small>
            </div>
          )}
          <div className="schedule-panel__footer">
            <span>{chosen.length ? `${chosen.length} ${chosen.length === 1 ? 'horário novo' : 'horários novos'}, de ${formatShortDate(saoPauloParts(chosen[0].starts_at).date)} a ${formatShortDate(saoPauloParts(chosen.at(-1)!.starts_at).date)}. Os que já existem ficam como estão.` : planned.length ? 'Todos os dias foram tirados.' : chosenDays.length ? 'Nenhum horário novo nesse período.' : 'Escolha os dias da semana.'}</span>
            <div>
              <button type="button" className="button button--secondary" onClick={() => setPanel(null)}>Cancelar</button>
              <button type="button" className="button button--primary" disabled={!chosen.length} onClick={() => add(chosen)}>Adicionar {chosen.length || ''} {chosen.length === 1 ? 'horário' : 'horários'}</button>
            </div>
          </div>
        </div>
      )}

      {panel === 'extend' && (
        <div className="schedule-panel" aria-label="Estender temporada">
          <p className="schedule-panel__pattern"><strong>Padrão da temporada:</strong> {describePattern(pattern)}{last ? `, até ${formatShortDate(last)}` : ''}.</p>
          <div className="schedule-panel__grid">
            <label className="field"><span>Estender até</span><input type="date" value={until} min={resumesFrom} onChange={(event) => setUntil(event.target.value)} /></label>
          </div>
          <div className="schedule-panel__footer">
            <span>{extension.length ? `${extension.length} ${extension.length === 1 ? 'horário novo' : 'horários novos'} no mesmo padrão.` : last && last >= today ? 'Escolha uma data depois do último horário.' : 'Escolha uma data a partir de hoje.'}</span>
            <div>
              <button type="button" className="button button--secondary" onClick={() => setPanel(null)}>Cancelar</button>
              <button type="button" className="button button--primary" disabled={!extension.length} onClick={() => add(extension)}>Estender até {until ? formatShortDate(until) : ''}</button>
            </div>
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div className="selection-bar" role="toolbar" aria-label="Horários selecionados">
          <span>{selected.length} {selected.length === 1 ? 'horário selecionado' : 'horários selecionados'}</span>
          <div>
            <button type="button" className="button button--quiet" onClick={() => setSelected([])}>Desmarcar</button>
            <button type="button" className="button button--secondary selection-bar__remove" onClick={() => askRemove(selected)}><Trash2 size={15} /> Remover</button>
          </div>
        </div>
      )}

      <div className={`occurrence-list ${published ? '' : 'occurrence-list--new'}`}
        onFocus={() => setHeld((current) => current ?? { past: idsOf(past), coming: idsOf(coming) })}
        onBlur={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
          setHeld(null)
          setOpened(null)
        }}>
        {selectable.length > 1 && (
          <label className="select-all">
            <input type="checkbox" checked={allSelected} ref={(node) => { if (node) node.indeterminate = someSelected }}
              onChange={() => setSelected(allSelected ? [] : selectable.map((item) => item.id))} />
            Selecionar todos
          </label>
        )}
        {past.length > 0 && (
          <button type="button" className="past-toggle" onClick={() => setShowPast((value) => !value)}>
            {showPast ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showPast ? 'Ocultar' : 'Mostrar'} {past.length} {past.length === 1 ? 'horário passado' : 'horários passados'}
          </button>
        )}
        {showPast && past.map(row)}
        {coming.map(row)}
        {!sorted.length && <div className="empty-inline">Nenhum horário ainda. Adicione uma data ou várias de uma vez.</div>}
      </div>

      {removing && (
        <RemoveSessionsDialog
          count={removing.length}
          calendarAdds={run.occurrences.filter((item) => removing.includes(item.id) && upcoming(item)).map((item) => calendarAdds?.[item.id] ?? 0)}
          place={run.place_name}
          onCancelSessions={() => remove(removing, 'canceled')}
          onDelete={() => remove(removing)}
          onClose={() => setRemoving(null)}
        />
      )}
    </>
  )
}
